/* eslint-disable camelcase */
import gql from 'graphql-tag';
import {
  Fragment_CourseCardBigThumb,
  Fragment_ClassCard,
  Fragment_MeditationCard,
  Fragment_ArticleCard,
  Fragment_TeacherCard,
  Fragment_LiveCard,
} from './generated';

export const Fragment_CurrentUserCourse_Completion = gql`
  fragment Fragment_CurrentUserCourse_Completion on CurrentUserCourse {
    id
    completion_percent
    is_complete
    total_minutes_watched
    completed_modules {
      id
    }
  }
`;

export const Fragment_CurrentUserStripeInvoice = gql`
  fragment Fragment_CurrentUserStripeInvoice on CurrentUserStripeInvoice {
    id
    payment_intent {
      status
      client_secret
    }
  }
`;

export const Fragment_CurrentUserStripeSubscription = gql`
  fragment Fragment_CurrentUserStripeSubscription on CurrentUserStripeSubscription {
    id
    trial_end
    trial_start
    status
    start_date
    customer
    plan_id
    plan_interval
    coupon
    trial_duration_days
    eligible_for_trial_extension
    eligible_for_scholarship
    eligible_for_annual_upgrade
    latest_invoice {
      ...Fragment_CurrentUserStripeInvoice
    }
    currency_code
  }
  ${Fragment_CurrentUserStripeInvoice}
`;

export const Fragment_CurrentUserSubscription = gql`
  fragment Fragment_CurrentUserSubscription on CurrentUserSubscription {
    tier
    plan_interval
    is_trial
    token
    created_at
    account_id
    plan_id
    currency_code
    product_name
    sub_status
    coupon_active
    coupon_id
    gateway
    next_assessment_at
    next_assessment_amount
    gateway_shortname
    last4
    card_type
    payment_type
    masked_card_number
    allow_billing_update
    cancel_at_period_end
    country
    price_override
    last_transaction_status
    migration_status
    plan {
      name
      is_trial
      tier
      plan_interval
      start_date
      sku
    }
    next_plan {
      name
      is_trial
      tier
      plan_interval
      start_date
      sku
    }
  }
`;

export const Fragment_Playlist = gql`
  fragment Fragment_Playlist on Playlist {
    id
    title
    user_id
    description
    items {
      id
      content {
        id
        title
        ... on Class {
          level {
            name
          }
          media_duration {
            group
            minutes
          }
          style {
            name
          }
        }
        ... on Meditation {
          media_duration {
            group
            minutes
          }
          style {
            name
          }
        }
        teacher {
          id
          full_name
        }
        __typename
        localized_path
        square_image(params: { width: 350 }) {
          processed_url
        }
      }
    }
  }
`;

// Only the fields on currentUser which need to be updated after membership changes
export const Fragment_CurrentUser_MembershipSubscription = gql`
  fragment Fragment_CurrentUser_MembershipSubscription on CurrentUser {
    id
    gaia_id
    account_issues
    membership
    membership_is_valid
    membership_tier
    membership_plan_interval
    subscription_gateway
    subscription_gateway_shortname
    subscription_id
    subscription_token
  }
`;

export const Fragment_CurrentUser = gql`
  fragment Fragment_CurrentUser on CurrentUser {
    id
    gaia_id
    email
    first_name
    last_name
    image(params: { width: 150 }) {
      processed_url
    }
    bio
    address
    address2
    company
    country
    state
    city
    zip
    phone
    is_approved_community_partner
    interests {
      id
      name
    }
    experience
    class_goal
    day_streak_current
    week_streak_current
    week_streak_longest
    class_count_current_week
    class_count_all_time
    class_minutes_all_time
    meditation_goal
    meditation_count_current_week
    meditation_count_all_time
    meditation_minutes_all_time
    class_count_current_month
    class_count_prev_month
    meditation_count_current_month
    meditation_count_prev_month
    class_minutes_current_month
    class_minutes_prev_month
    meditation_minutes_current_month
    meditation_minutes_prev_month
    class_level {
      id
      name
    }
    class_intensity {
      id
      name
    }
    user_goals {
      id
      name
    }
    yoga_teacher
    account_issues
    membership
    membership_is_valid
    membership_tier
    membership_plan_interval
    subscription_gateway
    subscription_gateway_shortname
    subscription_id
    subscription_token
    in_progress_classes {
      id
      completion_percent
    }
    in_progress_meditations {
      id
      completion_percent
    }
    purchased_courses {
      course {
        id
      }
    }
    in_progress_courses {
      course {
        id
      }
      completion_percent
    }
    completed_courses {
      course {
        id
        title
        duration {
          text
        }
      }
      recent_activity_date {
        rss
        yyyymmdd
      }
    }
    ltv_membership
    ltv_courses
    ltv_total
    remote_auth_s3
    favorite_classes {
      id
      created {
        timestamp
      }
      order
      content {
        id
      }
    }
    favorite_meditations {
      id
      created {
        timestamp
      }
      order
      content {
        id
      }
    }
    favorite_articles {
      id
      created {
        timestamp
      }
      order
      content {
        id
      }
    }

    favorite_teachers {
      id
      created {
        timestamp
      }
      order
      content {
        id
      }
    }
    favorite_playlists {
      id
      created {
        timestamp
      }
      order
      content {
        id
      }
    }
    recent_class_activity {
      id
      content {
        ...Fragment_ClassCard
        square_image(params: { width: 100 }) {
          processed_url
        }
      }
      minutes
      date {
        yyyymmdd
      }
    }
    recent_series_activity {
      id
      content {
        id
        title
        seasonNum
        episodeNum
        seriesSlug
        localized_path
        media_duration {
          seconds
          minutes
        }
        cast
        thumbnail {
          processed_url
        }
      }
      minutes
      date {
        yyyymmdd
      }
    }
    recent_meditation_activity {
      id
      content {
        ...Fragment_MeditationCard
      }
      minutes
      date {
        yyyymmdd
      }
    }
    recent_user_created_activity {
      id
      type
      minutes
      date {
        yyyymmdd
      }
    }
    primary_language
    most_recent_active_course {
      completion_percent
      is_complete
      course {
        ...Fragment_CourseCardBigThumb
      }
    }
  }
  ${Fragment_MeditationCard}
  ${Fragment_ClassCard}
  ${Fragment_CourseCardBigThumb}
`;

export const Fragment_CurrentUser_Favorites = gql`
  fragment Fragment_CurrentUser_Favorites on CurrentUser {
    id
    favorite_classes {
      id
      created {
        timestamp
      }
      order
      content {
        id
        square_image(params: { width: 350 }) {
          processed_url
        }
        ...Fragment_ClassCard
      }
    }
    favorite_meditations {
      id
      created {
        timestamp
      }
      order
      content {
        id
        ...Fragment_MeditationCard
      }
    }
    favorite_articles {
      id
      created {
        timestamp
      }
      order
      content {
        id
        square_image(params: { width: 350 }) {
          processed_url
        }
        ...Fragment_ArticleCard
      }
    }

    favorite_teachers {
      id
      created {
        timestamp
      }
      order
      content {
        id
        ...Fragment_TeacherCard
      }
    }

    favorite_playlists {
      id
      created {
        timestamp
      }
      order
      content {
        id
        ...Fragment_Playlist
      }
    }
  }
  ${Fragment_MeditationCard}
  ${Fragment_ClassCard}
  ${Fragment_ArticleCard}
  ${Fragment_TeacherCard}
  ${Fragment_Playlist}
`;
export const Fragment_Order = gql`
  fragment Fragment_Order on Order {
    id
    title
    authorized
    failed
    declined
    error
    coupon
    total
    items {
      id
      title
      price
    }
  }
`;

export const Fragment_CurrentUserLiveRegistration = gql`
  fragment Fragment_CurrentUserLiveRegistration on CurrentUserLiveRegistration {
    registrant_id
    content_id
    join_url
    content {
      id
      ...Fragment_LiveCard
    }
  }
  ${Fragment_LiveCard}
`;

export const Fragment_AllCurrentUserLiveRegistrations = gql`
  fragment Fragment_AllCurrentUserLiveRegistrations on CurrentUserLiveRegistrations {
    currentUserLiveRegistrations {
      registrant_id
      content_id
      join_url
      content {
        id
        ...Fragment_LiveCard
      }
    }
  }
  ${Fragment_LiveCard}
`;

export const Fragment_RegisterLiveError = gql`
  fragment Fragment_RegisterLiveError on RegisterLiveError {
    error_code
    status_code
    message
  }
`;

export const Fragment_CurrentUserLiveRegistrationError = gql`
  fragment Fragment_CurrentUserLiveRegistrationError on CurrentUserLiveRegistrationError {
    error_code
    status_code
    message
  }
`;
